import React from 'react';
import { Product, ProductOption, WaitlistEntry } from '../types';
import { Plus, Minus, Lock } from 'lucide-react';
import {
  formatArrivalDateLabel,
  sortArrivalDateValues,
} from '../utils/arrivalDates';
import WaitlistQuantityControl from './WaitlistQuantityControl';

interface ProductCardProps {
  product: Product;
  cart: Record<string, number>;
  onSetQuantity: (productId: string, quantity: number) => void;
  onQuickOrder?: (productId: string) => void;
  onViewDetail: (product: Product) => void;
  storeId?: string;
  showArrivalDateBadges?: boolean;
  orderedQuantities?: Record<string, number>;
  isOrdering?: boolean;
  waitlistsByKey?: Record<string, WaitlistEntry>;
  waitlistQuantities?: Record<string, number>;
  updatingWaitlistKey?: string | null;
  onSetWaitlistQuantity?: (
    productId: string,
    productOptionId: string | undefined,
    quantity: number,
  ) => void;
  onToggleWaitlist?: (
    productId: string,
    productOptionId?: string,
    quantity?: number,
  ) => void;
}

const sanitizeImageUrl = (url?: string) => {
  if (!url) return undefined;
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  return url;
};

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  cart,
  onSetQuantity,
  onQuickOrder,
  onViewDetail,
  showArrivalDateBadges = false,
  orderedQuantities = {},
  isOrdering = false,
  waitlistsByKey = {},
  waitlistQuantities = {},
  updatingWaitlistKey = null,
  onSetWaitlistQuantity,
  onToggleWaitlist,
}) => {
  const hasOptions = product.options && product.options.length > 0;

  const now = new Date();
  const salesStart = product.salesStartDate ? new Date(product.salesStartDate) : null;
  const isNotStarted = salesStart && salesStart > now;
  const arrivalDateLabels = showArrivalDateBadges
    ? sortArrivalDateValues(product.arrivalDateValues)
        .map((value) => formatArrivalDateLabel(value))
        .filter(Boolean)
    : [];
  const productImageUrl = sanitizeImageUrl(product.image);

  const renderWaitlistButton = (
    productId: string,
    productOptionId: string | undefined,
    waitlistCount = 0,
  ) => {
    if (!onToggleWaitlist) {
      return null;
    }

    const waitlistKey = productOptionId || productId;
    const entry = waitlistsByKey[waitlistKey];
    const isUpdating = updatingWaitlistKey === waitlistKey;
    const waitlistQuantity = waitlistQuantities[waitlistKey] || 1;

    return (
      <WaitlistQuantityControl
        quantity={waitlistQuantity}
        onQuantityChange={(quantity) =>
          onSetWaitlistQuantity?.(productId, productOptionId, quantity)
        }
        onToggle={() =>
          onToggleWaitlist(productId, productOptionId, waitlistQuantity)
        }
        waitlistEntry={entry}
        waitingQuantity={waitlistCount}
        disabled={isUpdating}
      />
    );
  };

  const renderOptionRow = (opt: ProductOption) => {
    const quantity = cart[opt.id] || 0;
    const orderedQuantity = orderedQuantities[opt.id] || 0;
    const stock = opt.availableStock ?? opt.stock ?? 0;
    const isSoldOut = stock === 0;
    const isClosed = opt.isClosed;
    const displayPrice = opt.price ?? product.price;
    const isQuickOrderMode = Boolean(onQuickOrder);

    const handleDelta = (e: React.MouseEvent, delta: number) => {
      e.stopPropagation();
      if (isSoldOut || isClosed || isNotStarted) return;
      const newQty = Math.max(0, Math.min(stock, quantity + delta));
      onSetQuantity(opt.id, newQty);
    };

    return (
      <div
        key={opt.id}
        className="bg-gray-50/80 rounded-[20px] p-4 flex items-center justify-between gap-3 mb-2 last:mb-0 border border-transparent active:border-gray-200 transition-all"
      >
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-black text-gray-900 truncate mb-0.5">
            {opt.name}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-bold text-gray-600">{displayPrice.toLocaleString()}원</span>
            <span className={`text-[11px] font-black ${isSoldOut ? 'text-red-500' : 'text-gray-400'}`}>
              (재고: {isSoldOut ? '품절' : isClosed ? '마감' : `${stock}개`})
            </span>
          </div>
        </div>

        {(!isSoldOut && !isClosed && !isNotStarted) ? (
          isQuickOrderMode ? (
            <div className="flex flex-col items-end gap-1">
              {orderedQuantity > 0 && (
                <span className="text-[10px] font-black text-emerald-600">
                  주문내역 {orderedQuantity}개
                </span>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onQuickOrder?.(opt.id);
                }}
                disabled={isOrdering}
                className="flex h-10 items-center gap-1.5 rounded-xl bg-emerald-500 px-3.5 text-[12px] font-black text-white shadow-sm shadow-emerald-100 transition-all active:scale-[0.98] disabled:bg-gray-300 disabled:shadow-none"
              >
                <Plus size={14} strokeWidth={3} />
                주문
              </button>
            </div>
          ) : (
          <div className="flex flex-col items-end gap-1">
            {orderedQuantity > 0 && (
              <span className="text-[10px] font-black text-emerald-600">
                주문내역 {orderedQuantity}개
              </span>
            )}
            <div className="flex items-center bg-white rounded-xl border border-gray-200 shadow-sm h-10 px-1">
              <button
                onClick={(e) => handleDelta(e, -1)}
                disabled={quantity === 0}
                className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${quantity === 0 ? 'text-gray-200' : 'text-gray-900'}`}
              >
                <Minus size={16} strokeWidth={3} />
              </button>
              <div className="min-w-[32px] text-center">
                <span className={`text-[15px] font-black ${quantity > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                  {quantity}개
                </span>
              </div>
              <button
                onClick={(e) => handleDelta(e, 1)}
                disabled={quantity >= stock}
                className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${quantity >= stock ? 'text-gray-200' : 'text-gray-900'}`}
              >
                <Plus size={16} strokeWidth={3} />
              </button>
            </div>
          </div>
          )
        ) : isSoldOut && !isClosed && !isNotStarted ? (
          renderWaitlistButton(product.id, opt.id, opt.waitlistCount)
        ) : (
          <div className="px-3 py-1.5 bg-gray-200/50 rounded-xl">
            <span className="text-[11px] font-black text-gray-400">선택 불가</span>
          </div>
        )}
      </div>
    );
  };

  const renderArrivalDateBadges = () => {
    if (arrivalDateLabels.length === 0) {
      return null;
    }

    const visibleLabels = arrivalDateLabels.slice(0, 2);
    const hiddenCount = Math.max(0, arrivalDateLabels.length - visibleLabels.length);

    return (
      <div className="mt-2 flex flex-wrap gap-1.5">
        {visibleLabels.map((label) => (
          <span
            key={label}
            className="rounded-full border border-sky-100 bg-sky-50 px-2 py-1 text-[10px] font-black text-sky-700"
          >
            {label}
          </span>
        ))}
        {hiddenCount > 0 && (
          <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-[10px] font-black text-gray-500">
            +{hiddenCount}
          </span>
        )}
      </div>
    );
  };

  const renderCancellationNotice = () => {
    if (product.isCustomerCancellationEnabled !== false) {
      return null;
    }

    return (
      <div className="mt-2 flex items-center gap-1 text-[10px] font-black text-orange-600">
        <Lock size={11} />
        주문 후 취소 불가
      </div>
    );
  };

  const renderProductImage = (className: string, grayscale = false) => {
    if (!productImageUrl) {
      return (
        <div className="flex h-full w-full items-center justify-center bg-gray-100 text-gray-300">
          <Lock size={20} />
        </div>
      );
    }

    return (
      <img
        src={productImageUrl}
        alt={product.name}
        className={`${className} ${grayscale ? 'grayscale opacity-60' : ''}`}
      />
    );
  };

  // Options product layout
  if (hasOptions) {
    const totalStock = product.options!.reduce((sum, o) => sum + (o.availableStock ?? o.stock ?? 0), 0);
    const minPrice = Math.min(...product.options!.map(o => o.price ?? product.price));

    return (
      <div className="mx-6 border-b py-6 flex flex-col gap-4">
        {/* Header */}
        <div className="flex gap-x-4 items-start" onClick={() => onViewDetail(product)}>
          <div className="relative w-28 h-28 flex-shrink-0 rounded-2xl overflow-hidden border border-gray-100 shadow-sm bg-gray-50 cursor-pointer">
            {renderProductImage('w-full h-full object-cover')}
            <div className="absolute top-2 left-2 bg-gray-900/80 backdrop-blur-md text-white text-[9px] font-black px-2 py-0.5 rounded-full">
              SET
            </div>
          </div>

          <div className="flex-1 min-w-0 cursor-pointer">
            <div className="space-y-0.5">
              <div className="flex items-start">
                <span className="text-[11px] text-gray-400 font-medium min-w-[45px] mt-0.5">상품명:</span>
                <span className="text-[13px] font-bold text-gray-800 line-clamp-2 leading-tight hover:text-emerald-600 transition-colors">
                  {product.name}
                </span>
              </div>
              {renderArrivalDateBadges()}
              {renderCancellationNotice()}
              <div className="flex items-center">
                <span className="text-[11px] text-gray-400 font-medium min-w-[45px]">가격:</span>
                <span className="text-[14px] font-black text-gray-900">{minPrice.toLocaleString()}원~</span>
              </div>
              <div className="flex items-center">
                <span className="text-[11px] text-gray-400 font-medium min-w-[45px]">재고:</span>
                <span className={`text-[11px] font-bold ${totalStock === 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                  {totalStock === 0 ? '품절됨' : `${totalStock}개 남음`}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Options */}
        <div className="flex flex-col gap-1">
          {product.options!.map(opt => renderOptionRow(opt))}
        </div>
      </div>
    );
  }

  // Single product layout (original style)
  const quantity = cart[product.id] || 0;
  const orderedQuantity = orderedQuantities[product.id] || 0;
  const stock = product.availableStock ?? product.totalStock ?? 0;
  const isSoldOut = stock === 0;
  const isClosed = product.isClosed;
  const isQuickOrderMode = Boolean(onQuickOrder);

  const handleDelta = (e: React.MouseEvent, delta: number) => {
    e.stopPropagation();
    if (isSoldOut || isClosed || isNotStarted) return;
    const newQty = Math.max(0, Math.min(stock, quantity + delta));
    onSetQuantity(product.id, newQty);
  };

  return (
    <div
      className="mx-6 border-b py-6 flex gap-x-4 items-center group cursor-pointer relative"
      onClick={() => onViewDetail(product)}
    >
      {/* Left: Product Image */}
      <div className="relative w-28 h-28 flex-shrink-0 rounded-2xl overflow-hidden border border-gray-100 shadow-sm bg-gray-50">
        {renderProductImage(
          'w-full h-full object-cover transition-transform duration-500 group-hover:scale-110',
          isSoldOut || isClosed || isNotStarted,
        )}
        {(isSoldOut || isClosed || isNotStarted) && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <span className="text-white text-[10px] font-black border border-white px-1.5 py-0.5 rounded shadow-sm">
              {isClosed ? 'ORDER CLOSED' : (isSoldOut ? 'SOLD OUT' : '판매 예정')}
            </span>
          </div>
        )}
        {!isSoldOut && !isClosed && !isNotStarted && product.images && product.images.length > 1 && (
          <div className="absolute bottom-1.5 right-1.5 bg-black/60 backdrop-blur-sm text-white px-1.5 py-0.5 rounded-lg">
            <span className="text-[9px] font-black">1/{product.images.length}</span>
          </div>
        )}
      </div>

      {/* Right: Product Info & Quantity Selector */}
      <div className="flex-1 flex flex-col justify-between min-h-[112px]">
        <div className="space-y-0.5">
          <div className="flex items-start">
            <span className="text-[11px] text-gray-400 font-medium min-w-[45px] mt-0.5">상품명:</span>
            <span className="text-[13px] font-bold text-gray-800 line-clamp-2 leading-tight group-hover:text-emerald-600 transition-colors">
              {product.name}
            </span>
          </div>
          {renderArrivalDateBadges()}
          {renderCancellationNotice()}
          <div className="flex items-center">
            <span className="text-[11px] text-gray-400 font-medium min-w-[45px]">가격:</span>
            <span className="text-[14px] font-black text-gray-900">{product.price.toLocaleString()}원</span>
          </div>
          <div className="flex items-center">
            <span className="text-[11px] text-gray-400 font-medium min-w-[45px]">재고:</span>
            <span className={`text-[11px] font-bold ${(isSoldOut || isClosed || isNotStarted) ? 'text-red-500' : 'text-emerald-500'}`}>
              {isClosed ? '영업 마감' : (isSoldOut ? '품절됨' : (isNotStarted ? `${salesStart!.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })} 판매 시작` : `${stock}개 남음`))}
            </span>
          </div>
        </div>

        {/* Quantity Selector */}
        <div className="flex items-center justify-end mt-2">
          {(!isSoldOut && !isClosed && !isNotStarted) ? (
            isQuickOrderMode ? (
              <div className="flex flex-col items-end gap-1">
                {orderedQuantity > 0 && (
                  <span className="text-[10px] font-black text-emerald-600">
                    주문내역 {orderedQuantity}개
                  </span>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuickOrder?.(product.id);
                  }}
                  disabled={isOrdering}
                  className="flex h-9 items-center gap-1.5 rounded-full bg-emerald-500 px-3.5 text-[12px] font-black text-white shadow-sm shadow-emerald-100 transition-all active:scale-[0.98] disabled:bg-gray-300 disabled:shadow-none"
                >
                  <Plus size={14} strokeWidth={3} />
                  주문
                </button>
              </div>
            ) : (
            <div className="flex flex-col items-end gap-1">
              {orderedQuantity > 0 && (
                <span className="text-[10px] font-black text-emerald-600">
                  주문내역 {orderedQuantity}개
                </span>
              )}
              <div
                className={`flex items-center rounded-full border transition-all h-8 px-1.5 ${quantity > 0 ? 'bg-emerald-50 border-emerald-300 shadow-sm' : 'bg-gray-50 border-gray-200'}`}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={(e) => handleDelta(e, -1)}
                  disabled={quantity === 0}
                  className={`p-1 rounded-full transition-colors ${quantity === 0 ? 'text-gray-300' : 'text-emerald-600 hover:bg-emerald-100'}`}
                >
                  <Minus size={14} strokeWidth={3} />
                </button>
                <div className="min-w-[32px] text-center">
                  <span className={`text-sm font-black transition-colors ${quantity > 0 ? 'text-emerald-700' : 'text-gray-400'}`}>
                    {quantity}
                  </span>
                </div>
                <button
                  onClick={(e) => handleDelta(e, 1)}
                  disabled={quantity >= stock}
                  className={`p-1 rounded-full transition-colors ${quantity >= stock ? 'text-gray-300' : 'text-emerald-600 hover:bg-emerald-100'}`}
                >
                  <Plus size={14} strokeWidth={3} />
                </button>
              </div>
            </div>
            )
          ) : isSoldOut && !isClosed && !isNotStarted ? (
            renderWaitlistButton(product.id, undefined, product.waitlistCount)
          ) : (
            <div className="flex items-center gap-1 px-2.5 py-1 bg-gray-100 rounded-full">
              <Lock size={10} className="text-gray-400" />
              <span className="text-[10px] font-bold text-gray-400">주문 불가</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
